import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, AlertTriangle } from "lucide-react";

const EXAMPLES = [
  "Carte / Carte de visite", "Flyer", "Faire-part", "Invitation", "Carte postale",
  "Marque-page", "Affiche", "Réglette", "Fiche", "Support pédagogique",
  "Dépliant", "Autre format",
];

const INCLUDED = [
  "Création graphique originale à partir de vos idées",
  "Composition, choix typographiques, direction visuelle",
  "Préparation prépresse : dimensions, résolution, colorimétrie",
  "Fonds perdus & export inclus lorsqu'ils sont nécessaires",
  "Fichier final HD prêt imprimeur",
  "2 séries de modifications mineures incluses",
];

export default function CreerMonSupport() {
  return (
    <div data-testid="page-creer-mon-support">
      {/* Hero */}
      <section className="cs-cream border-b border-black/10 py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">Parcours · Création graphique</div>
          <h1 className="cs-h1 mt-4 text-5xl lg:text-7xl">Création graphique<br /><span className="italic font-light text-[#0E7C90]">d'un support.</span></h1>
          <p className="mt-8 font-serif text-2xl lg:text-3xl max-w-3xl leading-snug">Vous avez l'idée. Charbonier Studio lui donne forme.</p>
          <p className="mt-6 text-neutral-700 max-w-2xl text-[15px] sm:text-base leading-relaxed">
            Imaginons ensemble votre support de A à Z. Vous partagez vos idées, vos textes et vos visuels ; Charbonier Studio imagine
            la création, réalise le design et vous livre un fichier prêt à être utilisé ou envoyé à l'imprimeur.
          </p>
        </div>
      </section>

      {/* Un support, pas un multipage */}
      <section className="cs-cream py-10 border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="border border-amber-400 bg-amber-50/60 p-5 flex items-start gap-4" data-testid="multipage-warning">
            <AlertTriangle size={22} className="text-amber-800 shrink-0 mt-1" />
            <div className="flex-1 text-[15px] leading-relaxed">
              <div className="font-serif text-xl mb-1">Un support, pas un document multipage.</div>
              <p className="text-neutral-800">
                Cette prestation concerne la création d'un support unique, éventuellement recto-verso ou plié.
                Pour un livre, livret, brochure multipage, catalogue, magazine, revue ou autre document composé de pages,
                utilisez la rubrique « Mise en page & édition ».
              </p>
              <Link to="/mise-en-page" className="mt-3 inline-flex items-center gap-1 font-mono text-[12px] uppercase tracking-[0.14em] text-amber-900 hover:text-black" data-testid="link-edition">
                Mettre en page un document <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Exemples */}
      <section className="cs-cream py-16 border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">Exemples de créations</div>
          <h2 className="cs-h2 mt-3 text-3xl lg:text-4xl">Une seule prestation, tous ces supports.</h2>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {EXAMPLES.map((e) => (
              <div key={e} className="border border-black/15 bg-white px-4 py-4 text-center text-[15px] font-serif" data-testid={`example-${e}`}>{e}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Tarif simple */}
      <section className="cs-cream py-16 border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-8 items-start">
          <div>
            <div className="cs-eyebrow text-neutral-500">Tarif</div>
            <h2 className="cs-h2 mt-3 text-3xl lg:text-4xl">Création graphique</h2>
            <div className="mt-6 font-serif text-5xl">À partir de 39 €</div>
            <p className="mt-4 text-[15px] text-neutral-700 leading-relaxed">
              <strong>39 €</strong> pour la première face ou le premier volet, puis <strong>+19 €</strong> par surface graphique supplémentaire.
            </p>
            <p className="mt-3 text-[13px] font-mono text-neutral-500 leading-relaxed">
              Le tarif dépend de la structure du support, pas de la difficulté artistique du visuel.
            </p>
          </div>
          <div className="border border-black/15 bg-white p-6 text-sm space-y-3" data-testid="pricing-examples">
            <div className="cs-eyebrow text-neutral-500">Exemples</div>
            <ul className="space-y-2">
              <li className="flex justify-between"><span>Flyer recto</span><span className="font-mono">39 €</span></li>
              <li className="flex justify-between"><span>Flyer recto-verso</span><span className="font-mono">58 €</span></li>
              <li className="flex justify-between"><span>Dépliant 1 pli, recto-verso (4 volets)</span><span className="font-mono">96 €</span></li>
              <li className="flex justify-between"><span>Dépliant 2 plis, recto-verso (6 volets)</span><span className="font-mono">134 €</span></li>
              <li className="flex justify-between"><span>Dépliant 3 plis, recto-verso (8 volets)</span><span className="font-mono">172 €</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Inclus */}
      <section className="cs-dark cs-grain py-20 border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="cs-eyebrow text-neutral-400">Ce qui est inclus</div>
            <ul className="mt-6 space-y-3">
              {INCLUDED.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-[15px]">
                  <Check size={18} className="mt-1 text-[#0E7C90]" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="cs-eyebrow text-neutral-400">Fichier prêt à imprimer</div>
            <p className="mt-6 text-[15px] text-neutral-300 leading-relaxed">
              La préparation technique finale est incluse : bonnes dimensions, résolution, colorimétrie, fonds perdus,
              paramètres d'impression et export final. Les fonds perdus ne sont jamais facturés en supplément.
            </p>
            <div className="cs-eyebrow text-neutral-400 mt-10">Aucun supplément sans votre accord</div>
            <p className="mt-3 text-[15px] text-neutral-300 leading-relaxed">
              Si votre demande dépasse le périmètre de la prestation, vous êtes prévenu avant toute intervention supplémentaire — jamais après.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cs-cream py-20 text-center">
        <div className="max-w-[1000px] mx-auto px-6">
          <h3 className="cs-h2 text-4xl lg:text-5xl">Prêt à créer votre support ?</h3>
          <p className="mt-4 max-w-xl mx-auto text-neutral-700 text-[15px] sm:text-base">
            Décrivez votre idée, joignez vos textes, images et inspirations. Charbonier Studio revient vers vous rapidement.
          </p>
          <div className="mt-8 flex justify-center flex-wrap gap-4">
            <Link to="/configurer/creation" className="cs-btn-primary" data-testid="creer-cta">
              Créer mon support <ArrowUpRight size={18} />
            </Link>
            <Link to="/demande-devis" className="cs-btn-ghost !text-black !border-black">J'ai une question</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
